/**
 * Game Completion Tracking Utility
 * 
 * Handles analytics and tracking for game completion
 * Extracted from App.jsx to improve code organization
 */

import { trackLearningProgress } from './learningAnalytics';
import { calculatePercentage } from './scoringUtils';

/**
 * Track game completion analytics
 * @param {Object} options - Completion tracking options
 * @param {string} options.currentScreen - Current subject/screen
 * @param {number} options.score - Game score
 * @param {number} options.questionCount - Number of questions
 * @param {boolean} options.hintUsed - Whether hint was used
 * @param {Function} options.trackEvent - Analytics trackEvent function (optional)
 * @returns {Promise<void>}
 */
export const trackGameCompletion = async ({
  currentScreen,
  score,
  questionCount,
  hintUsed = false,
  trackEvent = null
}) => {
  if (!currentScreen || score <= 0 || questionCount <= 0) {
    return;
  }

  const accuracy = calculatePercentage(score, questionCount);
  const totalTime = Date.now() - (window.gameStartTime || Date.now());

  // Track learning progress
  await trackLearningProgress(
    currentScreen,
    score,
    accuracy,
    totalTime,
    {
      questionCount,
      hintUsed
    }
  );

  // Track completion analytics if available
  if (trackEvent) {
    trackEvent('game_completed', {
      subject: currentScreen,
      score,
      accuracy,
      questionCount,
      totalTime,
      hintUsed
    });
  } else {
    // Lazy load analytics if not provided
    try {
      const { analyticsCollector } = await import('./analytics');
      analyticsCollector.trackEvent('game_completed', {
        subject: currentScreen,
        score,
        accuracy,
        questionCount,
        totalTime,
        hintUsed
      });
    } catch (error) {
      // Silently fail - analytics is not critical
    }
  }
};

/**
 * Track game drop-off (user exits mid-game)
 * @param {Object} options - Drop-off tracking options
 * @param {string} options.currentScreen - Current subject/screen
 * @param {number} options.currentQuestion - Current question index
 * @param {number} options.questionCount - Total question count
 * @param {Function} options.trackEvent - Analytics trackEvent function (optional)
 * @returns {Promise<void>}
 */
export const trackGameDropOff = async ({
  currentScreen,
  currentQuestion,
  questionCount,
  trackEvent = null
}) => {
  const validSubjects = ['phonics', 'math', 'reading', 'science', 'art', 'geography', 'history'];
  
  if (!currentScreen || !validSubjects.includes(currentScreen)) {
    return;
  }

  const totalTime = Date.now() - (window.gameStartTime || Date.now());

  if (trackEvent) {
    trackEvent('game_drop_off', {
      subject: currentScreen,
      questionIndex: currentQuestion,
      questionCount,
      totalTime
    });
  } else {
    // Lazy load analytics if not provided
    try {
      const { analyticsCollector } = await import('./analytics');
      analyticsCollector.trackEvent('game_drop_off', {
        subject: currentScreen,
        questionIndex: currentQuestion,
        questionCount,
        totalTime
      });
    } catch (error) {
      // Silently fail - analytics is not critical
    }
  }
};

/**
 * Update daily challenge progress based on game completion
 * @param {Object} options - Challenge update options
 * @param {Object} options.dailyChallengeData - Daily challenge data
 * @param {boolean} options.isChallengeCompleted - Whether challenge is already completed
 * @param {number} options.accuracy - Game accuracy percentage
 * @param {Function} options.updateChallengeProgress - Function to update challenge progress
 */
export const updateDailyChallengeFromGame = ({
  dailyChallengeData,
  isChallengeCompleted,
  accuracy,
  updateChallengeProgress
}) => {
  if (!dailyChallengeData || isChallengeCompleted || !updateChallengeProgress) {
    return;
  }

  if (dailyChallengeData.type === 'complete_subject' && accuracy >= 100) {
    updateChallengeProgress({ current: 1 });
  } else if (dailyChallengeData.type === 'perfect_score' && accuracy === 100) {
    updateChallengeProgress({ current: 1 });
  }
};

export default {
  trackGameCompletion,
  trackGameDropOff,
  updateDailyChallengeFromGame
};

